import { Howl, Howler } from 'howler';

/**
 * RÉMINISCENCE - Audio Manager
 * 
 * Gestion centralisée de la musique et des effets sonores.
 * Utilise Howler.js pour la compatibilité cross-browser.
 */

type MusicKey = 'landing' | 'game' | 'victory';
type SFXKey = 'click' | 'fragment' | 'shadow' | 'success' | 'error';

class AudioManager {
    private musicConfigs: Record<MusicKey, any> = {
        'landing': {
            src: ['/assets/audio/music/Reminiscence-EchosdesSouvenirs.mp3'],
            loop: true,
            preload: true,
            html5: true
        },
        'game': {
            src: ['/assets/audio/music/cortex.mp3'],
            loop: true,
            preload: true,
            html5: true
        },
        'victory': {
            src: ['/assets/audio/music/victory.mp3'],
            loop: false,
            preload: true,
            html5: true
        }
    };

    private sfxConfigs: Record<SFXKey, any> = {
        'click': { src: ['/assets/audio/sfx/click.mp3'] },
        'fragment': { src: ['/assets/audio/sfx/fragment.mp3'] },
        'shadow': { src: ['/assets/audio/sfx/shadow.mp3'] },
        'success': { src: ['/assets/audio/sfx/success.mp3'] },
        'error': { src: ['/assets/audio/sfx/error.mp3'] }
    };

    private music: Map<MusicKey, Howl> = new Map();
    private sfx: Map<SFXKey, Howl> = new Map();
    private isMuted: boolean = false;
    private musicVolume: number = 0.4;
    private sfxVolume: number = 0.6;
    private currentMusic: MusicKey | null = null;

    constructor() {
        this.loadSettings();
        this.setupInteractionListener();
    }

    private setupInteractionListener() {
        const resumeAudio = () => {
            // 1. Resume AudioContext (Web Audio API)
            if (Howler.ctx && Howler.ctx.state === 'suspended') {
                Howler.ctx.resume().then(() => {
                    console.log('🔊 AudioContext repris via interaction utilisateur');
                });
            }

            // 2. Play current music if valid and not playing (HTML5 Audio fallback)
            if (this.currentMusic) {
                const sound = this.music.get(this.currentMusic);
                if (sound && !sound.playing()) {
                    console.log(`🔊 Relance de la musique "${this.currentMusic}" après interaction`);
                    sound.play();
                    // Assurer le volume correct avec un fade-in
                    if (sound.volume() === 0) {
                        sound.fade(0, this.musicVolume, 1000);
                    }
                }
            }
        };

        // Wrapper pour nettoyer les écouteurs après la première interaction réussie
        const handleInteraction = () => {
            resumeAudio();
            ['click', 'keydown', 'touchstart'].forEach(event => {
                window.removeEventListener(event, handleInteraction);
            });
        };

        // Écouter les interactions pour débloquer l'audio
        ['click', 'keydown', 'touchstart'].forEach(event => {
            window.addEventListener(event, handleInteraction);
        });
    }

    /**
     * Charge tous les assets audio
     */
    private getOrLoadMusic(key: MusicKey): Howl | null {
        if (this.music.has(key)) return this.music.get(key)!;
        const config = this.musicConfigs[key];
        if (!config) return null;

        console.log(`💿 Lazy Loading Musique: ${key}`);
        const sound = new Howl({
            ...config,
            volume: this.musicVolume,
            onload: () => console.log(`✅ Musique "${key}" chargée`),
            onloaderror: (id, error) => console.error(`❌ Erreur chargement "${key}":`, error),
            onplay: () => console.log(`▶️ Musique "${key}" démarrée`),
            onstop: () => console.log(`⏸️ Musique "${key}" arrêtée`),
        });
        this.music.set(key, sound);
        return sound;
    }

    private getOrLoadSFX(key: SFXKey): Howl | null {
        if (this.sfx.has(key)) return this.sfx.get(key)!;
        const config = this.sfxConfigs[key];
        if (!config) return null;

        const sound = new Howl({
            ...config,
            volume: this.sfxVolume,
            preload: true
        });
        this.sfx.set(key, sound);
        return sound;
    }

    /**
     * Charge les paramètres depuis localStorage
     */
    private loadSettings() {
        const savedMuted = localStorage.getItem('audioMuted');
        const savedMusicVolume = localStorage.getItem('musicVolume');
        const savedSFXVolume = localStorage.getItem('sfxVolume');

        if (savedMuted === 'true') {
            this.isMuted = true;
            Howler.mute(true);
        }

        if (savedMusicVolume) {
            this.musicVolume = parseFloat(savedMusicVolume);
            this.music.forEach(sound => sound.volume(this.musicVolume));
        }

        if (savedSFXVolume) {
            this.sfxVolume = parseFloat(savedSFXVolume);
            this.sfx.forEach(sound => sound.volume(this.sfxVolume));
        }
    }

    /**
     * Joue une musique d'ambiance
     */
    /**
     * Joue une musique d'ambiance
     */
    playMusic(key: MusicKey) {
        // Tenter de reprendre le contexte Audio s'il est suspendu (Politique Autoplay Chrome)
        if (Howler.ctx && Howler.ctx.state === 'suspended') {
            Howler.ctx.resume().then(() => {
                console.log('🔊 AudioContext repris avec succès');
            });
        }

        // Si la même musique est déjà en cours, on s'assure juste qu'elle joue
        if (this.currentMusic === key) {
            const sound = this.music.get(key);
            if (sound && !sound.playing()) {
                sound.play();
            }
            return;
        }

        console.log(`🎵 Demande de lecture musique: ${key} (Précédente: ${this.currentMusic})`);

        // Fade out de l'ancienne musique si elle existe et est différente
        if (this.currentMusic) {
            const oldSound = this.music.get(this.currentMusic);
            if (oldSound) {
                oldSound.fade(this.musicVolume, 0, 1000);
                setTimeout(() => {
                    oldSound.stop();
                    oldSound.volume(this.musicVolume); // Reset volume pour la prochaine fois
                }, 1000);
            }
        } else {
            // Si c'est la première musique ou redémarrage, on stop tout brutalement par sécurité
            this.stopAllMusic();
        }

        // Jouer la nouvelle musique
        const sound = this.getOrLoadMusic(key);
        if (sound) {
            sound.volume(0); // Commencer à 0 pour fade in
            sound.play();
            sound.fade(0, this.musicVolume, 1000); // Fade in doux
            this.currentMusic = key;
        }
    }

    /**
     * Arrête toute musique
     */
    stopAllMusic() {
        this.music.forEach(sound => sound.stop());
        this.currentMusic = null;
    }

    /**
     * Fade out de la musique actuelle
     */
    fadeOutMusic(duration: number = 1000) {
        if (this.currentMusic) {
            const sound = this.music.get(this.currentMusic);
            if (sound) {
                sound.fade(this.musicVolume, 0, duration);
                setTimeout(() => {
                    sound.stop();
                    sound.volume(this.musicVolume);
                }, duration);
            }
        }
    }

    /**
     * Joue un effet sonore
     */
    playSFX(key: SFXKey) {
        if (!this.isMuted) {
            const sound = this.getOrLoadSFX(key);
            if (sound) {
                // Arrêter l'instance précédente si elle joue encore
                sound.stop();
                sound.play();
            }
        }
    }

    /**
     * Toggle mute/unmute
     */
    toggleMute(): boolean {
        this.isMuted = !this.isMuted;
        Howler.mute(this.isMuted);
        localStorage.setItem('audioMuted', this.isMuted.toString());
        return this.isMuted;
    }

    /**
     * Définit le volume de la musique
     */
    setMusicVolume(volume: number) {
        this.musicVolume = Math.max(0, Math.min(1, volume));
        this.music.forEach(sound => sound.volume(this.musicVolume));
        localStorage.setItem('musicVolume', this.musicVolume.toString());
    }

    /**
     * Définit le volume des SFX
     */
    setSFXVolume(volume: number) {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
        this.sfx.forEach(sound => sound.volume(this.sfxVolume));
        localStorage.setItem('sfxVolume', this.sfxVolume.toString());
    }

    /**
     * Getters
     */
    get muted(): boolean {
        return this.isMuted;
    }

    get currentMusicVolume(): number {
        return this.musicVolume;
    }

    get currentSFXVolume(): number {
        return this.sfxVolume;
    }

    /**
     * Nettoyage complet (pour HMR ou destruction)
     */
    cleanup() {
        this.stopAllMusic();
        Howler.unload();
    }
}

// Interface globale pour le singleton
declare global {
    interface Window {
        __AUDIO_MANAGER__?: AudioManager;
    }
}

// Instance singleton avec gestion HMR
let audioManagerInstance: AudioManager;

if (window.__AUDIO_MANAGER__) {
    // Si une instance existe déjà (HMR), on la nettoie
    console.log('♻️ Nettoyage de l\'ancien AudioManager pour HMR');
    window.__AUDIO_MANAGER__.cleanup();
}

audioManagerInstance = new AudioManager();
window.__AUDIO_MANAGER__ = audioManagerInstance;

export const audioManager = audioManagerInstance;
